import { Banner, ErrorBoundary, Text } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { type ColorMode } from 'app/utils/colorMode';
import { HtmlRenderer } from 'components/htmlRenderer';
import { ShadowDom, ShadowDomProps } from 'components/shadowDom';

import emailRendererShadowDomStylesheet from './assets/styles/emailRendererShadowDom.scss?source';
import emailReplyQuoteStylesheet from './assets/styles/emailReplyQuote.scss?source';
import { replaceReplyQuote } from './utils/replaceReplyQuote';

type HTMLShadowDomRendererProps = {
  /** The html content. */
  html: string;
  /** The color mode for rendering the HTML. */
  colorMode?: ColorMode;
} & Pick<ShadowDomProps, 'stylesheets'>;

/**
 * Renders HTML inside a shadow dom to prevent css conflicts. Commong quote
 * formats are moved into collapsible quote sections.
 */
export const HTMLShadowDomRenderer = ({
  html,
  stylesheets = [],
  colorMode,
}: HTMLShadowDomRendererProps): JSX.Element => {
  const mixedStylesheets = useMemo(
    () => [
      emailRendererShadowDomStylesheet,
      emailReplyQuoteStylesheet,
      ...stylesheets,
    ],
    [stylesheets],
  );

  return (
    <ErrorBoundary
      fallback={
        <Banner
          variant="fail"
          headline="Failed to display the content"
          description={
            <Text.Paragraph variant="4">
              An error occurred displaying the content. Our engineers have been
              notified and the issue will be investigated.
            </Text.Paragraph>
          }
        />
      }
    >
      <ShadowDom
        stylesheets={mixedStylesheets}
        {...(colorMode && {
          'data-color-mode': colorMode,
        })}
      >
        <HtmlRenderer
          html={html}
          sanitizationProfileType="email"
          replace={replaceReplyQuote}
        />
      </ShadowDom>
    </ErrorBoundary>
  );
};
